import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatApiErrorDetail } from '@/lib/authApi';
import { RESET_PASSWORD } from '@/constants/testIds';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const data = await resetPassword(token, password);
      toast.success(data.message || 'Password updated');
      navigate('/login', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Set a new password"
      subtitle="Choose a new password for your account"
      footer={<Link to="/login" className="text-primary font-medium hover:underline">Back to sign in</Link>}
    >
      {!token ? (
        <p className="text-sm text-destructive" data-testid="reset-password-no-token">
          This reset link is missing its token. Request a new one from the Forgot password page.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
          <div className="space-y-2">
            <Label htmlFor="password">New password</Label>
            <Input id="password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" data-testid={RESET_PASSWORD.passwordInput} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm password</Label>
            <Input id="confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" data-testid={RESET_PASSWORD.passwordConfirmInput} />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="reset-password-error">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading} data-testid={RESET_PASSWORD.submitButton}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update password'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
